/**
 * ApplicationStackCard — groups resources for a single application stack.
 *
 * Shows the application name, environment, aggregate health badge, and
 * compact rows for each EC2, RDS, and ECS resource (BR-U5-08 through BR-U5-12).
 */
import type { ApplicationStack } from '@/types';
import { StatusBadge } from './primitives/StatusBadge';
import { Ec2CompactRow } from './cards/Ec2CompactRow';
import { RdsCompactRow } from './cards/RdsCompactRow';
import { EcsCompactRow } from './cards/EcsCompactRow';
import styles from './ApplicationStackCard.module.css';

export interface ApplicationStackCardProps {
  stack: ApplicationStack;
}

export function ApplicationStackCard({ stack }: ApplicationStackCardProps) {
  const totalResources =
    stack.ec2Instances.length + stack.rdsInstances.length + stack.ecsClusters.length;

  return (
    <article className={styles.card} aria-label={`${stack.applicationName} — ${stack.environment}`}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.appName}>{stack.applicationName}</h2>
          <span className={styles.environment}>{stack.environment}</span>
        </div>
        <StatusBadge status={stack.aggregateHealthStatus} size="md" />
      </header>

      <div className={styles.meta}>
        {totalResources} resource{totalResources !== 1 ? 's' : ''}
      </div>

      {stack.ec2Instances.length > 0 && (
        <section className={styles.section}>
          <h3 className={styles.sectionHeading}>EC2 ({stack.ec2Instances.length})</h3>
          <ul className={styles.resourceList} role="list">
            {stack.ec2Instances.map((instance) => (
              <li key={instance.resourceId}>
                <Ec2CompactRow instance={instance} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {stack.rdsInstances.length > 0 && (
        <section className={styles.section}>
          <h3 className={styles.sectionHeading}>RDS ({stack.rdsInstances.length})</h3>
          <ul className={styles.resourceList} role="list">
            {stack.rdsInstances.map((instance) => (
              <li key={instance.resourceId}>
                <RdsCompactRow instance={instance} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {stack.ecsClusters.length > 0 && (
        <section className={styles.section}>
          <h3 className={styles.sectionHeading}>ECS ({stack.ecsClusters.length})</h3>
          <ul className={styles.resourceList} role="list">
            {stack.ecsClusters.map((cluster) => (
              <li key={cluster.resourceId}>
                <EcsCompactRow cluster={cluster} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}
